import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { StatusModal } from "components/DesignSystem/StatusModal/StatusModal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { Info } from "components/icons/Info";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useTypedFormikContext } from "hooks/useTypedFormikContext";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import {
  AlarmRule,
  AlarmRulePayload,
  useCreateAlarmRuleMutation,
  useUpdateAlarmRuleMutation,
} from "store/apis/alarm";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { useGetCountryStatesQuery } from "store/apis/teamSetting";
import { BackendError } from "types/utils/error";
import { alarmRuleSchema } from "formValidations/alarmRuleSchema";
import { US_UUID } from "constants/regAgent";
import {
  DATE_TYPE_LABELS,
  DATE_TYPE_DESCRIPTIONS,
  DEADLINE_DAY_OPTIONS,
  FIRST_DEADLINE_LOGIC_OPTIONS,
  TAXED_AS_OPTIONS,
} from "constants/alarmRules";

const labelWithTooltip = (label: string, tooltip: string, required = false) => (
  <div className="t-flex t-items-center t-gap-1">
    <span>
      {label}
      {required && <span className="t-font-bold t-text-red-50"> *</span>}
    </span>
    <ConditionalToolTip condition={tooltip} side="right">
      <Info size="14" color="currentColor" className="t-text-text-30" />
    </ConditionalToolTip>
  </div>
);

const DATE_TYPE_OPTIONS: OptionData[] = Object.entries(DATE_TYPE_LABELS).map(
  ([value, title]) => ({
    value,
    label: (
      <div className="t-flex t-flex-col t-gap-0.5">
        <span>{title}</span>
        <span className="t-text-body-sm t-text-text-30 t-whitespace-normal t-font-normal">
          {DATE_TYPE_DESCRIPTIONS[Number(value)]}
        </span>
      </div>
    ),
  })
);

type FormValues = {
  name: string;
  display_name: string;
  us_state: string | null;
  formation_type: string | null;
  date_type: string;
  deadline_date: string;
  deadline_day: string;
  deadline_month: string;
  first_deadline_logic: string;
  taxed_as: string;
  approx_price: string;
};

const toFormValues = (rule: AlarmRule): FormValues => ({
  name: rule.name,
  display_name: rule.display_name ?? "",
  us_state: rule.us_state ?? null,
  formation_type: rule.formation_type ?? null,
  date_type: String(rule.date_type),
  deadline_date: rule.deadline_date != null ? String(rule.deadline_date) : "",
  deadline_day: rule.deadline_day != null ? String(rule.deadline_day) : "0",
  deadline_month:
    rule.deadline_month != null ? String(rule.deadline_month) : "",
  first_deadline_logic: String(rule.first_deadline_logic),
  taxed_as: String(rule.taxed_as),
  approx_price: rule.approx_price ?? "",
});

const defaultValues: FormValues = {
  name: "",
  display_name: "",
  us_state: null,
  formation_type: null,
  date_type: "0",
  deadline_date: "",
  deadline_day: "0",
  deadline_month: "",
  first_deadline_logic: "0",
  taxed_as: "0",
  approx_price: "",
};

const toPayload = (values: FormValues): Partial<AlarmRulePayload> => ({
  name: values.name,
  display_name: values.display_name || null,
  date_type: Number(values.date_type),
  deadline_date: values.deadline_date ? Number(values.deadline_date) : null,
  deadline_day: Number(values.deadline_day),
  deadline_month:
    values.deadline_month !== "" ? Number(values.deadline_month) : null,
  first_deadline_logic: Number(values.first_deadline_logic),
  taxed_as: Number(values.taxed_as),
  approx_price: values.approx_price || null,
  us_state: values.us_state ?? null,
  formation_type: values.formation_type ?? null,
});

type Props = {
  open: boolean;
  onClose: () => void;
  rule?: AlarmRule;
};

const FormBody = ({
  stateOptions,
  formationTypeOptions,
  isLoading,
  onClose,
  isEditing,
}: {
  stateOptions: { label: string; value: string }[];
  formationTypeOptions: { label: string; value: string }[];
  isLoading: boolean;
  onClose: () => void;
  isEditing: boolean;
}) => {
  const { values, setFieldValue, isValid, dirty } =
    useTypedFormikContext<FormValues>();

  return (
    <>
      <Modal.Body>
        <div className="t-flex t-flex-col t-gap-4 t-overflow-y-auto t-max-h-[60vh]">
          <Combobox
            label="State"
            required
            menuPortalTarget={document.body}
            options={stateOptions}
            value={
              stateOptions.find((o) => o.value === values.us_state) ?? null
            }
            onChange={(opt) =>
              setFieldValue(
                "us_state",
                (opt as { value: string } | null)?.value ?? null
              )
            }
            isClearable
          />

          <Combobox
            label="Formation type"
            required
            menuPortalTarget={document.body}
            options={formationTypeOptions}
            value={
              formationTypeOptions.find(
                (o) => o.value === values.formation_type
              ) ?? formationTypeOptions[0]
            }
            onChange={(opt) =>
              setFieldValue(
                "formation_type",
                (opt as { value: string | null } | null)?.value ?? null
              )
            }
          />

          <TextInput
            name="name"
            label="Rule name"
            required
            tooltipText="Unique internal identifier for this rule. Not visible to customers."
            placeholder="e.g. California_SOI_Corp"
          />

          <TextInput
            name="display_name"
            label="Display name"
            required
            tooltipText="Filing name shown to customers in the app. Falls back to rule name if left empty."
            placeholder="e.g. California Statement of Information"
          />

          <Combobox
            label={labelWithTooltip(
              "Date type",
              "What the deadline is calculated relative to — tax year end, incorporation month, or a fixed calendar date.",
              true
            )}
            menuPortalTarget={document.body}
            options={DATE_TYPE_OPTIONS}
            value={
              DATE_TYPE_OPTIONS.find((o) => o.value === values.date_type) ??
              null
            }
            onChange={(opt) =>
              setFieldValue(
                "date_type",
                (opt as { value: string } | null)?.value ?? "0"
              )
            }
          />

          <div className="t-grid t-grid-cols-2 t-gap-4">
            <TextInput
              name="deadline_date"
              label="Deadline date"
              tooltipText="Day of the month (1–31) the filing is due. Leave blank if using Deadline day."
              placeholder="e.g. 15"
              type="number"
              min={1}
              max={31}
            />

            <Combobox
              label="Deadline day"
              tooltipText='A relative due day such as "last day of month." Use instead of Deadline date when the day isn&apos;t fixed.'
              menuPortalTarget={document.body}
              options={DEADLINE_DAY_OPTIONS}
              value={
                DEADLINE_DAY_OPTIONS.find(
                  (o) => o.value === values.deadline_day
                ) ?? null
              }
              onChange={(opt) =>
                setFieldValue(
                  "deadline_day",
                  (opt as { value: string } | null)?.value ?? "0"
                )
              }
            />
          </div>

          <TextInput
            name="deadline_month"
            label="Deadline month offset"
            tooltipText="1–12: filing is due X months after the base date. 13–24: maps to a specific calendar month (13 = January, 24 = December)."
            placeholder="e.g. 3"
            type="number"
          />

          <Combobox
            label="First deadline logic"
            tooltipText="Overrides the standard deadline for a company's very first filing. Leave as None if no special first-year rule applies."
            menuPortalTarget={document.body}
            options={FIRST_DEADLINE_LOGIC_OPTIONS}
            value={
              FIRST_DEADLINE_LOGIC_OPTIONS.find(
                (o) => o.value === values.first_deadline_logic
              ) ?? null
            }
            onChange={(opt) =>
              setFieldValue(
                "first_deadline_logic",
                (opt as { value: string } | null)?.value ?? "0"
              )
            }
          />

          <Combobox
            label="Taxed as"
            tooltipText="Restricts this rule to a specific tax classification (Corp, Partnership, etc.). Set to None to apply to all entity types."
            menuPortalTarget={document.body}
            options={TAXED_AS_OPTIONS}
            value={
              TAXED_AS_OPTIONS.find((o) => o.value === values.taxed_as) ?? null
            }
            onChange={(opt) =>
              setFieldValue(
                "taxed_as",
                (opt as { value: string } | null)?.value ?? "0"
              )
            }
          />

          <TextInput
            name="approx_price"
            label="Approximate price"
            required
            tooltipText="Estimated filing fee shown to customers. For display only — does not affect any calculations."
            placeholder="e.g. 100"
          />
        </div>
      </Modal.Body>

      <Modal.FooterButtonGroup>
        <Button customType="secondary" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button
          customType="primary"
          type="submit"
          isLoading={isLoading}
          disabled={!isValid || !dirty}
        >
          {isEditing ? "Save changes" : "Create rule"}
        </Button>
      </Modal.FooterButtonGroup>
    </>
  );
};

export const CreateEditAlarmRuleModal = ({ open, onClose, rule }: Props) => {
  const { alertToast, successToast } = useToast();

  const { data: states = [] } = useGetCountryStatesQuery({
    countryId: US_UUID,
  });
  const { data: countries = [] } = useGetCountriesDataQuery();
  const formationTypes =
    countries.find((c) => c.uuid === US_UUID)?.formation_type ?? [];

  const stateOptions = states.map(({ name, state_id }) => ({
    label: name,
    value: state_id,
  }));
  const ALARM_RULE_FORMATION_TYPES = new Set([
    "C Corp",
    "S-Corp",
    "Limited Liability Company",
  ]);
  const formationTypeOptions = [
    { label: "All", value: null as unknown as string },
    ...formationTypes
      .filter(({ name }) => ALARM_RULE_FORMATION_TYPES.has(name))
      .map(({ name, uuid }) => ({ label: name, value: uuid })),
  ];

  const [createRule, { isLoading: isCreating }] = useCreateAlarmRuleMutation();
  const [updateRule, { isLoading: isUpdating }] = useUpdateAlarmRuleMutation();
  const isLoading = isCreating || isUpdating;
  const isEditing = Boolean(rule);
  const [pendingValues, setPendingValues] = useState<FormValues | null>(null);

  const handleSubmit = (values: FormValues) => {
    setPendingValues(values);
  };

  const handleConfirm = async () => {
    if (!pendingValues) return;
    const payload = toPayload(pendingValues);
    try {
      if (isEditing && rule) {
        await updateRule({ ruleUuid: rule.uuid, body: payload }).unwrap();
        successToast({ message: "Alarm rule updated successfully" });
      } else {
        await createRule(payload).unwrap();
        successToast({ message: "Alarm rule created successfully" });
      }
      setPendingValues(null);
      onClose();
    } catch (e) {
      alertToast({
        message:
          (e as BackendError).data?.error?.message ?? "Something went wrong",
      });
    }
  };

  const initialValues = rule ? toFormValues(rule) : defaultValues;

  return (
    <>
      <Modal.Root open={open} onOpenChange={(v) => !v && onClose()}>
        <Modal.Content useCustomOverlay size="regular">
          <Modal.Header>
            <Modal.Title>
              {isEditing ? "Edit alarm rule" : "Create alarm rule"}
            </Modal.Title>
            <Modal.Close />
          </Modal.Header>

          <FormikForm
            initialValues={initialValues}
            validationSchema={alarmRuleSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            <FormBody
              stateOptions={stateOptions}
              formationTypeOptions={formationTypeOptions}
              isLoading={isLoading}
              onClose={onClose}
              isEditing={isEditing}
            />
          </FormikForm>
        </Modal.Content>
      </Modal.Root>

      <Modal.Root
        open={Boolean(pendingValues)}
        onOpenChange={(v) => !v && setPendingValues(null)}
      >
        <Modal.Content useCustomOverlay size="small">
          <StatusModal
            type="warning"
            title={isEditing ? "Save changes?" : "Create rule?"}
          >
            {isEditing
              ? "This will update deadline calculations for all existing entities that match this rule's criteria."
              : "This will start applying deadline calculations to all entities that match this rule's criteria."}
          </StatusModal>
          <Modal.FooterButtonGroup>
            <Button
              customType="secondary"
              type="button"
              onClick={() => setPendingValues(null)}
            >
              Back
            </Button>
            <Button
              customType="primary"
              isLoading={isLoading}
              onClick={handleConfirm}
            >
              Confirm
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
